import type { IConwayError } from "conway-errors";
import { createError, isConwayError } from "conway-errors";
import type { ILogErrorToSentryParams } from "@litres/utils";
import { hasWindow, logErrorToSentry } from "@litres/utils";
import { errorPostfixMakers } from "./errorPostfixMakers";

export * from "./originalErrorTypes";

interface CustomExtendedParams
  extends Record<string, string | number | boolean | undefined> {
  isSSR: boolean;
  projectName: string;
  subdomain: string;
  logLevel: ILogErrorToSentryParams["logLevel"];
}

const createErrorContext = createError(
  [
    {
      /**
       * Ошибка в логике работы программы.
       * Пример - cannot read property of undefined.
       *
       * Если ловим такие ошибки - нужно исправлять фронтенд код.
       */
      errorType: "FrontendLogicError",
      createMessagePostfix: errorPostfixMakers.forErrorLikeObjects,
    },
    {
      /**
       * Ошибка при парсинге данных из URL/localStorage/IndexDB и т.п.
       * Пример - Карточка Книги была открыта с невалидными URL параметрами, из-за чего невозможно запросить данные об Арте.
       * Может быть пользователь сломал URL, а может он был неправильно сформирован при переходе с прошлой страницы.
       *
       * Если  ловим такие ошибки - нужно по их частоте/другим признакам понять, нужно ли что-то с ними делать. Так как пользователь сам может ломать URL, менять данные в браузерных хранилищах и т.п., мы не можем полностью избежать подобных ошибок, но они точно не должны происходить слишком часто.
       */
      errorType: "DataFromBrowserHandling",
      createMessagePostfix: errorPostfixMakers.forErrorLikeObjects,
    },
    {
      /**
       * Ошибка при взаимодействии с API Litres backend'а.
       * Например, backend при запросе выдал ошибку со статусом 503 или был сформирован неверный запрос.
       *
       * Если ловим такие ошибки - нужно либо править фронтенд код, либо общаться с бэкендерами для поиска проблемы на их стороне.
       */
      errorType: "BackendInteractionError",
      createMessagePostfix: errorPostfixMakers.forBackendInteraction,
    },
    {
      /**
       * Ожидаемая Ошибка в рамках бизнес логики.
       * Она должны быть обработаны в UI, но мы всё равно на всякий случай отправляем её в Sentry.
       * К примеру, произошла ошибка оплаты - у пользователя нет денег на карте. В UI мы эту ошибку оплаты обработали, но всё равно логируем в Sentry. Во-первых, чтобы увидеть, если вдруг ошибка стала появляться слишком часто. Во-вторых, просто приятно видеть такую статистику и сопоставлять кол-во ошибок бизнес логики с остальными.
       *
       * Если  ловим такие ошибки - нужно по их частоте/другим признакам понять, нужно ли что-то с ними делать.
       */
      errorType: "ExpectedBusinessFlowError",
    },
    { errorType: "AnalyticsError" }, // Ошибка при отправке аналитики.
  ] as const,
  {
    extendedParams: {
      isSSR: !hasWindow,
      projectName: "litres-frontend",
      logLevel: "error",
    },
    handleEmit: (error, extendedParams) => {
      const { logLevel, ...tags } = extendedParams as CustomExtendedParams;

      const sentryLoggingParams: ILogErrorToSentryParams = {
        tags,
        logLevel,
      };

      if (error.originalError) {
        sentryLoggingParams.contextProperties = [
          "original error",
          getOriginalInfoForLoggingContext(error.originalError),
        ];
      }

      if (process.env.NODE_ENV === "development") {
        logErrorToConsole(error, sentryLoggingParams);
      } else {
        logErrorToSentry(error, sentryLoggingParams);
      }
    },
  }
);

export const generalErrorContext = createErrorContext("General", {
  subdomain: "General",
});

export const acquisitionErrorContext = createErrorContext("Acquisition", {
  subdomain: "Acquisition",
});

export const monetizationErrorContext = createErrorContext("Monetization", {
  subdomain: "Monetization",
});

export const navigationErrorContext = createErrorContext("Navigation", {
  subdomain: "Navigation",
});

export const loyaltyErrorContext = createErrorContext("Loyalty", {
  subdomain: "Loyalty",
});

export type ILitresError = IConwayError;

/**
 * Type Guard, проверяющий, что пойманная ошибка - кастомная, созданная с помощью пакета litres/errors.
 * @param error Что угодно, что было поймано в блоке "catch".
 */
export const isLitresError = (error: unknown): error is ILitresError => {
  return isConwayError(error);
};

function getOriginalInfoForLoggingContext(
  originalError: unknown
): Record<string, unknown> {
  try {
    if (originalError instanceof Error) {
      return {
        value: JSON.stringify(
          originalError,
          Object.getOwnPropertyNames(originalError)
        ),
      };
    } else {
      return {
        value: JSON.stringify(originalError),
      };
    }
  } catch (error) {
    return {
      value: `Error stringifying original error object`,
    };
  }
}

function logErrorToConsole(
  error: ILitresError,
  sentryLoggingParams: ILogErrorToSentryParams
) {
  console.error(
    "/----------------------------/",
    `\n[DEV] Send "${sentryLoggingParams.logLevel}" event to sentry`,
    error,
    "tags:",
    sentryLoggingParams.tags,
    "\n/----------------------------/"
  );
}
